import { AutomationsPresentationV1Edge } from 'Automations/automationEditor'
import {
  BuildStructureFn,
  IntermediateNode
} from '../../presentationToConductorWorkflow.types'

type ResolveJoinNodes = (params: {
  forkNodeId: string
  branchTargets: Set<string>
  skippedNodeIds: Set<string>
  visitedSoFar: Set<string>
  branchNodes: IntermediateNode[][]
  branchPath: string
  isInsideParallelFork: boolean
  outgoingEdges: Map<string, AutomationsPresentationV1Edge[]>
  joinNodeIds: Set<string>
  incomingSources: Map<string, Set<string>>
  buildStructure: BuildStructureFn
}) => {
  continuation: IntermediateNode[]
  remainingSkipped: Set<string>
}

export const resolveJoinNodes: ResolveJoinNodes = params => {
  /**
   * Find all nodes reachable from the fork's branches, stopping at other
   * join nodes. The fork node itself is also reachable.
   */
  const reachable = getNodesReachableFrom({
    startNodes: params.branchTargets,
    outgoingEdges: params.outgoingEdges,
    stopNodes: params.joinNodeIds
  })
  reachable.add(params.forkNodeId)

  /**
   * A skipped node is "resolved" if all of its incoming sources are reachable
   * from this fork — meaning this fork fully owns it and it can be placed
   * after the join. Otherwise it stays skipped and bubbles up further.
   */
  const allSourcesReachable = (id: string) => {
    const sources = params.incomingSources.get(id) ?? new Set()
    return [...sources].every(src => reachable.has(src))
  }

  const skippedIds = [...params.skippedNodeIds]
  const resolvedIds = skippedIds.filter(allSourcesReachable)
  const remainingSkipped = new Set(
    skippedIds.filter(id => !allSourcesReachable(id))
  )

  /**
   * Build each resolved join node sequentially — each one adds to the
   * visited set so the next build knows what has already been processed.
   */
  const initialVisited = new Set([
    ...params.visitedSoFar,
    ...getBaseNodeIds(params.branchNodes.flat())
  ])

  const { continuation, skipped } = resolvedIds.reduce(
    (acc, joinNodeId) => {
      const result = params.buildStructure({
        nodeId: joinNodeId,
        visited: acc.visited,
        branchPath: params.branchPath,
        isInsideParallelFork: params.isInsideParallelFork
      })

      return {
        continuation: [...acc.continuation, ...result.intermediateNodes],
        visited: new Set([
          ...acc.visited,
          ...getBaseNodeIds(result.intermediateNodes)
        ]),
        skipped: new Set([...acc.skipped, ...result.skippedJoinNodes])
      }
    },
    {
      continuation: [] as IntermediateNode[],
      visited: initialVisited,
      skipped: new Set<string>()
    }
  )

  return {
    continuation,
    remainingSkipped: new Set([...remainingSkipped, ...skipped])
  }
}

const getNodesReachableFrom = ({
  startNodes,
  outgoingEdges,
  stopNodes
}: {
  startNodes: Set<string>
  outgoingEdges: Map<string, AutomationsPresentationV1Edge[]>
  stopNodes: Set<string>
}): Set<string> => {
  const reachable = new Set<string>()
  const queue = [...startNodes]

  while (queue.length > 0) {
    const nodeId = queue.shift()!

    if (reachable.has(nodeId)) continue
    reachable.add(nodeId)

    if (stopNodes.has(nodeId)) continue

    const edges = outgoingEdges.get(nodeId) ?? []
    edges.forEach(e => queue.push(e.target))
  }

  return reachable
}

export const getBaseNodeIds = (
  intermediateNodes: IntermediateNode[]
): Set<string> => {
  const collect = (nodes: IntermediateNode[]): string[] => {
    return nodes.flatMap(n => [
      n.node.id.split('__')[0],
      ...n.branches.flatMap(collect)
    ])
  }

  return new Set(collect(intermediateNodes))
}
